import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { api, setSeo, track } from "@/lib/api";
import { useCardView } from "@/lib/analytics";
import { Logo } from "@/components/site/Logo";
import { Loading } from "@/pages/NotFound";
import { SmartImage } from "@/components/site/SmartImage";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const PartnerCard = ({ card, categoryId }) => {
  const ref = useRef(null);
  const send = () =>
    track({
      event_type: "cta_click",
      cta_placement: "LINKS PAGE",
      page: "/links",
      links_section: categoryId,
      links_target: card.track_id,
      merchant: card.merchant,
    });
  useCardView(ref, {
    event_type: "partner_impression",
    cta_placement: "LINKS PAGE",
    page: "/links",
    links_section: categoryId,
    links_target: card.track_id,
    merchant: card.merchant,
  });

  return (
    <div
      ref={ref}
      data-testid={`links-card-${card.card_id || slug(card.merchant)}`}
      className="bg-white border border-line flex flex-col"
    >
      <a
        href={card.href}
        target="_blank"
        rel="sponsored nofollow noopener"
        aria-label={`Shop ${card.merchant}`}
        data-testid={`links-image-${card.card_id || slug(card.merchant)}`}
        onClick={send}
        className={`group block aspect-[16/10] overflow-hidden ${
          card.fit === "contain" ? "bg-[#F4F2ED]" : ""
        }`}
      >
        <SmartImage
          src={card.image}
          alt={card.image_alt}
          sizes="(min-width:1024px) 33vw, (min-width:640px) 50vw, 100vw"
          maxWidth={768}
          style={{ objectPosition: card.focus || "center" }}
          className={`w-full h-full group-hover:scale-[1.03] transition-transform duration-1000 ease-out ${
            card.fit === "contain" ? "object-contain p-4" : "object-cover"
          }`}
        />
      </a>

      <div className="px-5 md:px-6 pt-6 pb-6 flex flex-col flex-1">
        <h3 className="text-[19px] md:text-[20px] leading-tight font-medium text-charcoal">
          {card.merchant}
        </h3>
        <p className="text-[14.5px] md:text-[15px] leading-snug text-[#5E5A54] mt-2.5">
          {card.description}
        </p>
        <a
          href={card.href}
          target="_blank"
          rel="sponsored nofollow noopener"
          data-testid={`links-shop-${card.card_id || slug(card.merchant)}`}
          onClick={send}
          className="mt-auto flex items-center justify-center gap-2 bg-sage text-white text-[12.5px] tracking-[0.14em] uppercase font-semibold mt-6 px-5 min-h-[54px] hover:bg-sage-dark transition-colors duration-300"
        >
          {card.cta_label} <ArrowUpRight size={15} />
        </a>
      </div>
    </div>
  );
};

export default function LinksPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/links-page").then((r) => setData(r.data)).catch(() => {});
    track({ event_type: "page_view", page: "/links" });
  }, []);

  useEffect(() => {
    if (!data) return;
    setSeo({ title: data.meta_title, description: data.meta_description, noindex: true });
  }, [data]);

  if (!data) return <Loading label="Loading partners" />;

  return (
    <div data-testid="links-page" className="bg-[#FBFAF8] min-h-screen">
      <div className="max-w-[1120px] mx-auto px-5 md:px-8 pt-10 pb-16 md:pt-14 md:pb-24">
        <div className="max-w-[620px] mx-auto text-center">
          <div className="flex justify-center">
            <Logo />
          </div>
          <h1 className="display text-[31px] md:text-[40px] leading-[1.1] text-charcoal mt-8">
            {data.heading}
          </h1>
          <p className="text-[16px] md:text-[17px] leading-[1.6] text-[#454545] mt-4">
            {data.intro}
          </p>
        </div>

        {data.categories.map((group) => (
          <section
            key={group.id}
            data-testid={`links-section-${group.id}`}
            className="mt-12 md:mt-16"
          >
            <span className="eyebrow">{group.label}</span>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mt-5">
              {group.cards.map((card) => (
                <PartnerCard key={card.track_id} card={card} categoryId={group.id} />
              ))}
            </div>
          </section>
        ))}

        <div className="mt-14 md:mt-16 pt-8 border-t border-line max-w-[760px]">
          <Link
            to={data.secondary_cta.to}
            data-testid="links-secondary-cta"
            onClick={() =>
              track({ event_type: "cta_click", cta_placement: "LINKS PAGE", page: "/links",
                      links_target: "site" })
            }
            className="inline-flex items-center gap-2 text-[12.5px] tracking-[0.14em] uppercase font-semibold text-[#48563F] hover:text-sage-dark transition-colors duration-300"
          >
            {data.secondary_cta.label} <ArrowRight size={15} />
          </Link>
          <p
            data-testid="links-affiliate-disclosure"
            className="text-[13px] leading-relaxed text-[#5E5A54] mt-6"
          >
            {data.disclosure}
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5">
            <Link
              to="/affiliate-disclosure"
              data-testid="links-disclosure-link"
              className="text-[12.5px] tracking-[0.12em] uppercase font-semibold text-[#48563F] hover:text-sage-dark transition-colors"
            >
              Affiliate Disclosure
            </Link>
            <Link
              to="/editorial-standards"
              data-testid="links-standards-link"
              className="text-[12.5px] tracking-[0.12em] uppercase font-semibold text-[#48563F] hover:text-sage-dark transition-colors"
            >
              Editorial Standards
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
